import { APIError } from '../types/api';

export enum ApiErrorType {
  NETWORK = 'NETWORK',
  TIMEOUT = 'TIMEOUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN',
}

export interface ProcessedApiError {
  type: ApiErrorType;
  message: string;
  code: string;
  details: Array<{ field: string; message: string }>;
}

/**
 * Handle and categorize API errors
 */
export const handleApiError = (error: Error): ProcessedApiError => {
  // Check for network errors
  if (isNetworkError(error)) {
    return {
      type: ApiErrorType.NETWORK,
      message: 'Network connection failed. Please check your internet connection.',
      code: 'NETWORK_ERROR',
      details: []
    };
  }

  // Check for timeout errors
  if (error.name === 'AbortError' || error.message.includes('timeout')) {
    return {
      type: ApiErrorType.TIMEOUT,
      message: 'Request timed out. Please try again.',
      code: 'TIMEOUT_ERROR',
      details: []
    };
  }

  // Try to parse API error response
  try {
    const errorData = JSON.parse(error.message);
    
    if (errorData.error) {
      const apiError: APIError = errorData.error;
      const type = getErrorTypeFromCode(apiError.code);
      
      return {
        type,
        message: apiError.message,
        code: apiError.code,
        details: apiError.details || []
      };
    }

    // Check for HTTP status codes
    if (errorData.status) {
      const type = getErrorTypeFromStatus(errorData.status);
      return {
        type,
        message: createApiErrorMessage(type),
        code: `HTTP_${errorData.status}`,
        details: []
      };
    }
  } catch {
    // Not a JSON error, continue to unknown error handling
  }

  // Unknown error
  return {
    type: ApiErrorType.UNKNOWN,
    message: 'An unexpected error occurred. Please try again.',
    code: 'UNKNOWN_ERROR',
    details: []
  };
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: Error): boolean => {
  return (
    error.name === 'TypeError' ||
    error.message.includes('Failed to fetch') ||
    error.message.includes('Network error') ||
    error.message.includes('ERR_NETWORK')
  );
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: Error): boolean => {
  try {
    const errorData = JSON.parse(error.message);
    return errorData.error?.code === 'VALIDATION_ERROR';
  } catch {
    return false;
  }
};

/**
 * Format validation errors into user-friendly messages
 */
export const formatValidationErrors = (
  details: Array<{ field: string; message: string }>
): string[] => {
  return details.map(detail => {
    let fieldName = detail.field;
    
    // Handle special cases
    const specialCases: Record<string, string> = {
      'url': 'URL',
      'api': 'API',
      'id': 'ID',
      'uuid': 'UUID',
    };
    
    // Check if the entire field name is a special case
    if (specialCases[fieldName.toLowerCase()]) {
      fieldName = specialCases[fieldName.toLowerCase()];
    } else {
      // Add space before capital letters and capitalize first letter
      fieldName = fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    }
    
    return `${fieldName}: ${detail.message}`;
  });
};

/**
 * Create user-friendly error messages for different error types
 */
export const createApiErrorMessage = (type: ApiErrorType): string => {
  const messages = {
    [ApiErrorType.NETWORK]: 'Network connection failed. Please check your internet connection.',
    [ApiErrorType.TIMEOUT]: 'Request timed out. Please try again.',
    [ApiErrorType.UNAUTHORIZED]: 'You are not authorized to perform this action.',
    [ApiErrorType.FORBIDDEN]: 'Access denied. You do not have permission to perform this action.',
    [ApiErrorType.NOT_FOUND]: 'The requested resource was not found.',
    [ApiErrorType.VALIDATION]: 'Please check your input and try again.',
    [ApiErrorType.SERVER]: 'Server error occurred. Please try again later.',
    [ApiErrorType.UNKNOWN]: 'An unexpected error occurred. Please try again.',
  };

  return messages[type];
};

/**
 * Retry API call with exponential backoff
 */
export const retryApiCall = async <T>(
  apiCall: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error as Error;

      // Don't retry validation errors or client errors
      if (isValidationError(lastError) || isClientError(lastError)) {
        throw lastError;
      }

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Wait before retrying with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
};

/**
 * Get error type from API error code
 */
const getErrorTypeFromCode = (code: string): ApiErrorType => {
  const codeMap: Record<string, ApiErrorType> = {
    'VALIDATION_ERROR': ApiErrorType.VALIDATION,
    'UNAUTHORIZED': ApiErrorType.UNAUTHORIZED,
    'FORBIDDEN': ApiErrorType.FORBIDDEN,
    'API_NOT_FOUND': ApiErrorType.NOT_FOUND,
    'DUPLICATE_NAME': ApiErrorType.VALIDATION,
    'INVALID_API_ID': ApiErrorType.VALIDATION,
    'INTERNAL_SERVER_ERROR': ApiErrorType.SERVER,
  };

  return codeMap[code] || ApiErrorType.UNKNOWN;
};

/**
 * Get error type from HTTP status code
 */
const getErrorTypeFromStatus = (status: number): ApiErrorType => {
  if (status >= 400 && status < 500) {
    switch (status) {
      case 400:
        return ApiErrorType.VALIDATION;
      case 401:
        return ApiErrorType.UNAUTHORIZED;
      case 403:
        return ApiErrorType.FORBIDDEN;
      case 404:
        return ApiErrorType.NOT_FOUND;
      default:
        return ApiErrorType.VALIDATION;
    }
  }

  if (status >= 500) {
    return ApiErrorType.SERVER;
  }

  return ApiErrorType.UNKNOWN;
};

/**
 * Check if error is a client error (4xx) that shouldn't be retried
 */
const isClientError = (error: Error): boolean => {
  try {
    const errorData = JSON.parse(error.message);
    const status = errorData.status;
    return status >= 400 && status < 500;
  } catch {
    return false;
  }
};

/**
 * Delay function for retry logic
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
